import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search, FileText, Send, X, RotateCcw, Loader2, AlertTriangle, CalendarDays,
    ShieldCheck, FilePlus2, ArrowUpRight, FlaskConical, Stethoscope, User, Sparkles,
} from 'lucide-react';
import LabSummaryModal from '../../components/ai/LabSummaryModal';
import useTenantFeatures from '../../hooks/useTenantFeatures';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import usePermissions from '../../hooks/usePermissions';
import useSession from '../../hooks/useSession';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { hospitalPatientCode } from '../../utils/patientCode';
import { formatTime } from '../../utils/clock';
import {
    getReports, getReport, getReportPdf, resendReport,
} from '../../redux/labSlice';

// The laboratory's FILE OF PUBLISHED DOCUMENTS: find a report, read it, print
// it, send it again — and, when a number turns out to be wrong, start the
// amendment that supersedes it.
//
// NOTHING ON THIS SCREEN EDITS A REPORT, AND THAT IS THE WHOLE POINT.
// A released pathology report physically exists in the world: a clinician has
// read it, a patient holds a copy, somebody has already changed a dose on the
// strength of it. So the backend treats it as immutable from the moment it is
// released (api/controllers/labReportController.js) and a correction is a NEW
// version carrying its own number, its own signature and a pointer back to what
// it replaces. If this page offered an "edit" it would be lying about what the
// server does, and the version a clinician acted on would silently stop
// existing — which is precisely the record an inquiry asks for.
//
// DRAFTS ARE NOT LISTED HERE, EVER.
// A draft is the bench's working copy and may hold a value that is about to be
// repeated, corrected or withdrawn. The status filter below never offers one,
// because the moment a draft appears in a list next to released documents,
// somebody at the counter prints it for a patient who is standing there waiting.
// Work in progress is read on the worklist, where its unreleased state is
// unmistakable.
//
// THE SUPERSEDED WARNING IS THE MOST IMPORTANT THING ON THE ROW.
// The list legitimately holds version 1 and version 2 of one order side by side.
// The withdrawn one is exactly the one a reader in a hurry picks, so every row
// that has been overtaken says so, its resend is refused (the server refuses it
// too — REPORT_SUPERSEDED), and the current version is one click away.

// ------------------------------------------------------------------ api ----

// THERE IS NO API MAP IN THIS FILE, AND THAT IS THE FIX.
//
// This page used to keep its own four paths and its own axios calls beside
// redux/labSlice.js, which already had getReports, getReport, getReportPdf and
// resendReport. Two implementations of one endpoint do not stay identical: they
// drift, and the half that drifts is whichever one the next person did not open.
// The copy here had already lost something real — the slice reads the backend's
// structured refusals (409 REPORT_SUPERSEDED with the current version attached,
// 400 NO_RECIPIENT, 503 PDF_UNAVAILABLE meaning the report is fine and only the
// drawing failed), and a page that re-derives its own error text from
// `response.data.message` cannot act on any of them.
//
// So every call below dispatches a slice thunk. What each one answers with, and
// which refusals it carries, is documented on the thunk itself.
//
// `.unwrap()` is used rather than reading the slice's state because three of the
// four need the answer in the same tick — a PDF URL for a window that is already
// open, a report number for the confirmation, the superseding version for the
// jump — and because the drawer below shows a MERGE of the list row and the
// detail call that no single slice field models.

// Where the bench enters, verifies and amends values (LabResultEntry, which
// reads the order from the path). An amendment BEGINS there — a corrected result
// row — and only becomes a document when the order is released again, so the
// amend dialog on this page hands over rather than pretending it can do the work
// itself. One builder, because App.jsx owns the route table and a rename should
// be one edit.
const resultsRouteFor = (orderId) => `/lab/results/${encodeURIComponent(orderId)}`;

// ----------------------------------------------------------------- time ----

// Rendered in the BROWSER's zone. The server cuts the day in the hospital's own
// zone (that is what the from/to filter means), so a laptop set to another
// country can show a report under the neighbouring date. Acceptable because the
// report number, not the printed timestamp, is what anybody quotes back — but do
// not build a turnaround statistic out of these strings.
// The time part is the one clock format ("2:30 PM", utils/clock).
const DATE_ONLY = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
});
const stampOf = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '-' : `${DATE_ONLY.format(d)}, ${formatTime(d)}`;
};

const todayISO = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
const daysAgoISO = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Age AT COLLECTION, printed the way a paediatrician reads it.
 *
 * The server sends `ageDays` rather than a formatted string because the
 * reference bands were resolved in days — a six-week-old and a six-month-old are
 * judged against different haemoglobin ranges, and "0 years" beside both would
 * make the printed range look wrong for one of them. Under two years the months
 * are what mean something; over two, the years are.
 */
const ageLabel = (ageDays) => {
    const n = Number(ageDays);
    if (!Number.isFinite(n) || n < 0) return null;
    if (n < 31) return `${Math.floor(n)}d`;
    if (n < 730) {
        const months = Math.floor(n / 30.4375);
        const days = Math.max(0, Math.floor(n - months * 30.4375));
        return days ? `${months}m ${days}d` : `${months}m`;
    }
    return `${Math.floor(n / 365.25)}y`;
};

// --------------------------------------------------------------- labels ----

const STATUS_STYLES = {
    released: 'bg-green-50 text-green-700 border-green-200',
    amended: 'bg-purple-50 text-purple-700 border-purple-200',
    draft: 'bg-gray-100 text-gray-500 border-gray-200',
};

// The flag is the only thing on the page a clinician scans for, so the two that
// mean "telephone somebody now" are the only two that are loud. Colour alone
// never carries it — every chip also spells the flag out, because a red-green
// colour-blind reader is common and a critical potassium is not the place to
// find that out.
const FLAG_STYLES = {
    critical_low: 'bg-red-50 text-red-700 border-red-300 font-bold',
    critical_high: 'bg-red-50 text-red-700 border-red-300 font-bold',
    low: 'bg-amber-50 text-amber-700 border-amber-200',
    high: 'bg-amber-50 text-amber-700 border-amber-200',
    abnormal: 'bg-amber-50 text-amber-700 border-amber-200',
    normal: 'bg-gray-50 text-gray-500 border-gray-200',
};
const FLAG_LABELS = {
    critical_low: 'CRITICAL LOW',
    critical_high: 'CRITICAL HIGH',
    low: 'LOW',
    high: 'HIGH',
    abnormal: 'ABNORMAL',
    normal: 'NORMAL',
};

const CHANNELS = ['email', 'sms', 'whatsapp'];

const errorText = (error, fallback) => extractErrorMessage(error, fallback);

/** The interval a value was judged against, exactly as it was snapshotted. */
const referenceText = (r) => {
    const low = r.refLow === null || r.refLow === undefined || r.refLow === '' ? null : Number(r.refLow);
    const high = r.refHigh === null || r.refHigh === undefined || r.refHigh === '' ? null : Number(r.refHigh);
    if (Number.isFinite(low) && Number.isFinite(high)) return `${low} – ${high}`;
    if (Number.isFinite(low)) return `≥ ${low}`;
    if (Number.isFinite(high)) return `≤ ${high}`;
    // No band matched this patient's age and sex at collection, and the bench is
    // required NOT to guess one. An empty cell is the honest answer; inventing a
    // range would put a number in the "normal" column that nobody stands behind.
    return r.refText || '-';
};

/**
 * Group the results the way the PDF prints them: by bench, then by profile.
 *
 * The screen and the document have to agree, because a ward sister rings up
 * quoting "the third line under Biochemistry" and both of them are looking at
 * their own copy. Section order follows the master's sortOrder (which is what
 * the renderer uses); anything whose section has been closed since falls to the
 * end rather than disappearing.
 */
const groupResults = (results = [], sections = []) => {
    const rank = new Map(sections.map((s, i) => [s.sectionId, i]));
    const bySection = new Map();
    for (const row of results) {
        const key = row.sectionId || '__unsectioned';
        if (!bySection.has(key)) {
            bySection.set(key, { key, name: row.sectionName || null, rows: [] });
        }
        bySection.get(key).rows.push(row);
    }
    const blocks = [...bySection.values()];
    blocks.sort((a, b) => {
        const ai = rank.has(a.key) ? rank.get(a.key) : Number.MAX_SAFE_INTEGER;
        const bi = rank.has(b.key) ? rank.get(b.key) : Number.MAX_SAFE_INTEGER;
        return ai - bi;
    });
    // Inside a bench, components of one profile stay together under its name.
    // A panel is ORDERED as one line and MEASURED as several, so a CBC printed
    // as eleven loose analytes is unreadable to the person who ordered a CBC.
    return blocks.map((block) => {
        const byPanel = new Map();
        for (const row of block.rows) {
            const key = row.panelTestId || '__single';
            if (!byPanel.has(key)) {
                byPanel.set(key, { key, name: row.panelTestId ? row.panelName : null, rows: [] });
            }
            byPanel.get(key).rows.push(row);
        }
        return { ...block, panels: [...byPanel.values()] };
    });
};

export default function LabReports() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can, canAny } = usePermissions();
    const session = useSession();
    const [searchParams] = useSearchParams();

    // WHOSE LABORATORY. Ignored for anybody carrying a hospital in their verified
    // token; required for Super Admin, who carries none and is answered 400 until
    // the organisation is named on the URL. Sent on every call so one screen
    // serves both rather than growing a path only Super Admin exercises.
    const tenantId = searchParams.get('tenantId') || session.tenantId || '';

    // Sending a report puts a document full of clinical data in front of
    // somebody, so it sits behind its own permission server-side. Hiding the
    // button is the polite half only — api/routes/labReportRoutes.js is the gate.
    const canResend = can('lab.report.release');
    // A correction starts at the bench. Whoever may record or verify a value, or
    // release the set, is who would be told a result is wrong.
    const canAmend = canAny(['lab.result.enter', 'lab.result.verify', 'lab.report.release']);

    const [reports, setReports] = useState([]);
    const [total, setTotal] = useState(0);
    const [listLoading, setListLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // A month, not "everything". A lab report is asked about for far longer than
    // a bill — a patient comes back a fortnight later with the printout — but an
    // unbounded default would make the first paint scan every document the
    // hospital has ever issued.
    const [from, setFrom] = useState(() => daysAgoISO(29));
    const [to, setTo] = useState(() => todayISO());
    // Both published states by default. 'amended' is not a lesser document: it
    // is the CURRENT one for its order, and a filter that defaulted to
    // 'released' alone would hide every corrected report from the person most
    // likely to be looking for it.
    const [status, setStatus] = useState('released,amended');
    const [refreshKey, setRefreshKey] = useState(0);

    const [drawer, setDrawer] = useState(null);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [pdfBusyId, setPdfBusyId] = useState(null);
    const [resendTarget, setResendTarget] = useState(null);
    /* ---- AI pack: patient-friendly summary (Phase 4, #15) ---- */
    const { has: hasFeature } = useTenantFeatures();
    const [summaryReportId, setSummaryReportId] = useState(null);
    const [amendTarget, setAmendTarget] = useState(null);

    const searchTimeoutRef = useRef(null);

    // The server refuses an inverted range. Catching it here means the user sees
    // which field is wrong while they are still looking at it, rather than a
    // toast that says the request failed.
    const rangeInvalid = Boolean(from && to && to < from);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 500);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    useEffect(() => {
        // Nothing to ask for, and the spinner must not be left running — the
        // banner below is the whole answer until one of the dates moves.
        if (rangeInvalid) { setListLoading(false); return undefined; }
        let cancelled = false;
        setListLoading(true);
        (async () => {
            try {
                const body = await dispatch(getReports({
                    page: currentPage,
                    limit: itemsPerPage,
                    status,
                    search: debouncedSearch,
                    from,
                    to,
                    tenantId,
                })).unwrap();
                if (cancelled) return;
                setReports(body?.data || []);
                setTotal(body?.total || 0);
            } catch (error) {
                if (cancelled) return;
                setReports([]);
                setTotal(0);
                toast.warn(errorText(error, t('labreports.load_error', 'Failed to load the reports')));
            } finally {
                if (!cancelled) setListLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, tenantId, currentPage, itemsPerPage, debouncedSearch, status, from, to, rangeInvalid, refreshKey]);

    const refetch = () => setRefreshKey((k) => k + 1);

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const resetFilters = () => {
        setSearchQuery('');
        setStatus('released,amended');
        setFrom(daysAgoISO(29));
        setTo(todayISO());
        setCurrentPage(1);
    };

    /**
     * Open one report with its values.
     *
     * The row is shown immediately and the results fill in underneath: the
     * header — number, patient, signature, when it went out — is already on the
     * list row, so there is nothing to wait for before the drawer is useful.
     * Accepts a bare `{ reportId }` too, which is how the superseded chip jumps
     * straight to the version that replaced this one.
     */
    const openReport = useCallback(async (report) => {
        if (!report?.reportId) return;
        setDrawer({ report, results: [], sections: [] });
        setDrawerLoading(true);
        try {
            const full = await dispatch(getReport(report.reportId)).unwrap();
            if (full) {
                setDrawer({
                    ...full,
                    // The list row carries the patient's NAME; the detail call
                    // carries the identity block. Merged so the drawer header
                    // never blanks out for the second it takes to arrive.
                    report: { ...report, ...(full.report || {}) },
                });
            }
        } catch (error) {
            toast.warn(errorText(error, t('labreports.detail_error', 'Failed to load the report')));
        } finally {
            setDrawerLoading(false);
        }
    }, [dispatch, t]);

    const closeDrawer = useCallback(() => setDrawer(null), []);

    useEffect(() => {
        if (!drawer) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') closeDrawer(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawer, closeDrawer]);

    /**
     * Open the report PDF.
     *
     * THE TAB IS OPENED BEFORE THE REQUEST, not after it. The server draws the
     * document lazily on first ask and stores it, so this call can take a second
     * or two — and a window.open() that happens after an await has lost its user
     * gesture and is silently eaten by the pop-up blocker. Opening first and
     * pointing it at the URL afterwards is the only reliable order.
     */
    const handleViewPdf = async (report) => {
        setPdfBusyId(report.reportId);
        const win = window.open('', '_blank');
        // `opener` is severed by hand rather than through the 'noopener' feature
        // string, because that form makes window.open return null and there
        // would be nothing left to point at the URL.
        if (win) win.opener = null;

        try {
            const body = await dispatch(getReportPdf({ reportId: report.reportId })).unwrap();
            const url = body?.url;
            if (!url) throw new Error(t('labreports.pdf_missing', 'The report PDF is not available'));
            if (win) {
                win.location.href = url;
            } else {
                toast.info(t('labreports.popup_blocked', 'Allow pop-ups for this site to open the report PDF'));
            }
        } catch (error) {
            if (win) win.close();
            // 503 PDF_UNAVAILABLE means THE REPORT IS RELEASED and only the
            // drawing failed. The server's wording says so; passing it through
            // matters, because a lab that reads this as "the release did not
            // happen" releases it a second time.
            toast.warn(errorText(error, t('labreports.pdf_error', 'Failed to produce the report PDF')));
        } finally {
            setPdfBusyId(null);
        }
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    return (
        <div data-testid="lab-reports-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="lab-reports-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('labreports.title', 'Lab reports')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('labreports.subtitle', 'Every report the laboratory has signed and released. Reports are read and re-sent here — a correction is issued as a new version, never as an edit to one already in the world.')}
                    </p>
                </div>
                <button
                    data-testid="lab-reports-refresh"
                    onClick={refetch}
                    disabled={listLoading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 whitespace-nowrap ${listLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    <RotateCcw className="w-4 h-4" />
                    {t('labreports.refresh', 'Refresh')}
                </button>
            </div>

            {/* ------------------------------------------------- filters ---- */}
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
                <div className="relative lg:col-span-2">
                    <input
                        data-testid="reports-search-input"
                        type="text"
                        placeholder={t('labreports.search_placeholder', 'Report no / order id / patient id…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 50))}
                        maxLength={50}
                        className="w-full p-2 pl-10 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                <div className="relative">
                    <input
                        data-testid="reports-from-date"
                        type="date"
                        value={from}
                        max={to || undefined}
                        onChange={(e) => { setFrom(e.target.value); setCurrentPage(1); }}
                        className={`w-full p-2 pl-9 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${rangeInvalid ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    <CalendarDays className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>
                <div className="relative">
                    <input
                        data-testid="reports-to-date"
                        type="date"
                        value={to}
                        min={from || undefined}
                        onChange={(e) => { setTo(e.target.value); setCurrentPage(1); }}
                        className={`w-full p-2 pl-9 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${rangeInvalid ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    <CalendarDays className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                {/* No 'draft' option, on purpose — see the file header. */}
                <select
                    data-testid="reports-status-filter"
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="released,amended">{t('labreports.all_published', 'Released & amended')}</option>
                    <option value="released">{t('labreports.first_issue', 'First issue only')}</option>
                    <option value="amended">{t('labreports.amended_only', 'Amended only')}</option>
                </select>

                <button
                    data-testid="reports-reset-filters"
                    onClick={resetFilters}
                    className="w-full p-2 rounded-xl border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer"
                >
                    {t('labreports.reset', 'Reset filters')}
                </button>
            </div>

            {rangeInvalid && (
                <div data-testid="reports-range-error" className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {t('labreports.range_error', 'The “to” date is earlier than the “from” date, so there is nothing to show.')}
                </div>
            )}

            {/* ------------------------------------------------ the list ---- */}
            {listLoading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('labreports.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="reports-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labreports.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labreports.report', 'Report')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labreports.patient', 'Patient')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labreports.signed_by', 'Signed by')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('labreports.released_at', 'Released')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('labreports.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="reports-table-body">
                                {reports.length > 0 ? reports.map((r, index) => {
                                    const superseded = r.supersededBy || null;
                                    return (
                                        <tr
                                            key={r.reportId}
                                            data-testid={`report-row-${r.reportId}`}
                                            className={`border-b border-gray-100/50 transition-all duration-300 ${superseded
                                                ? 'bg-amber-50/40 hover:bg-amber-50/70'
                                                : 'hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80'}`}
                                        >
                                            <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                            <td className="p-3 text-xs">
                                                <button
                                                    type="button"
                                                    data-testid={`open-report-${r.reportId}`}
                                                    onClick={() => openReport(r)}
                                                    className="font-semibold text-[#2D8A8A] hover:underline cursor-pointer font-mono"
                                                >
                                                    {r.reportNo}
                                                </button>
                                                <div className="flex items-center gap-1 mt-1">
                                                    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold capitalize ${STATUS_STYLES[r.status] || STATUS_STYLES.draft}`}>
                                                        {r.status}
                                                    </span>
                                                    {/* Only from version 2 — printing "v1" on a first
                                                        issue would suggest something had happened to it. */}
                                                    {Number(r.version) > 1 && (
                                                        <span className="inline-flex px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-[10px] font-semibold">
                                                            v{r.version}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-3 text-xs text-gray-600">
                                                <div className="font-semibold text-gray-700">{r.patientName || t('common.patient_label', 'Patient')}</div>
                                                {hospitalPatientCode(r) && <div className="text-[11px] text-slate-400 font-mono">{hospitalPatientCode(r)}</div>}
                                            </td>
                                            <td className="p-3 text-xs text-gray-600">
                                                <div className="font-semibold text-gray-700">{r.signedByName || '-'}</div>
                                                {/* The registration number is what makes the sheet a
                                                    pathology report rather than a printout, so it is
                                                    shown on the row and not only inside the document. */}
                                                <div className="text-[11px] text-slate-400">
                                                    {r.signedByRegNo
                                                        ? `${t('labreports.reg_no', 'Reg.')} ${r.signedByRegNo}`
                                                        : t('labreports.no_reg', 'no registration recorded')}
                                                </div>
                                            </td>
                                            <td className="p-3 text-xs text-gray-600">{stampOf(r.releasedAt)}</td>
                                            <td className="p-3 text-xs">
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        data-testid={`view-report-${r.reportId}`}
                                                        onClick={() => openReport(r)}
                                                        title={t('labreports.open', 'Open the report')}
                                                        className="text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md cursor-pointer transition-all duration-300 hover:scale-110"
                                                    >
                                                        <FlaskConical className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        data-testid={`view-report-pdf-${r.reportId}`}
                                                        onClick={() => handleViewPdf(r)}
                                                        disabled={pdfBusyId === r.reportId}
                                                        title={t('labreports.view_pdf', 'View PDF')}
                                                        className={`text-[#D7A05C] hover:text-white p-2 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 hover:from-[#D7A05C] hover:to-[#b8863f] shadow-md transition-all duration-300 hover:scale-110 ${pdfBusyId === r.reportId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                    >
                                                        {pdfBusyId === r.reportId
                                                            ? <Loader2 className="h-4 w-4 animate-spin" />
                                                            : <FileText className="h-4 w-4" />}
                                                    </button>
                                                    {/* AI pack (Phase 4, #15): plain-language summary of a
                                                        RELEASED report — the server enforces the release gate. */}
                                                    {hasFeature('ai_lab_summary') && r.releasedAt && (
                                                        <button
                                                            data-testid={`ai-summary-${r.reportId}`}
                                                            onClick={() => setSummaryReportId(r.reportId)}
                                                            title={t('labreports.ai_summary', 'Explain for the patient')}
                                                            className="text-[#2D8A8A] hover:text-white p-2 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#2D8A8A] shadow-md cursor-pointer transition-all duration-300 hover:scale-110"
                                                        >
                                                            <Sparkles className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                    {canResend && (
                                                        <button
                                                            data-testid={`resend-report-${r.reportId}`}
                                                            onClick={() => setResendTarget(r)}
                                                            disabled={!!superseded}
                                                            title={superseded
                                                                ? t('labreports.resend_superseded', 'This version has been corrected — send the current one instead')
                                                                : t('labreports.resend', 'Send this report again')}
                                                            className={`text-[#2D8A8A] hover:text-white p-2 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#2D8A8A] shadow-md transition-all duration-300 hover:scale-110 ${superseded ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                                                        >
                                                            <Send className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                    {canAmend && (
                                                        <button
                                                            data-testid={`amend-report-${r.reportId}`}
                                                            onClick={() => setAmendTarget(r)}
                                                            title={t('labreports.amend', 'Amend — issues a new version')}
                                                            className="text-purple-600 hover:text-white p-2 rounded-xl bg-gradient-to-r from-purple-50 to-fuchsia-50 hover:from-purple-500 hover:to-fuchsia-500 shadow-md cursor-pointer transition-all duration-300 hover:scale-110"
                                                        >
                                                            <FilePlus2 className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                                {superseded && (
                                                    <button
                                                        type="button"
                                                        data-testid={`open-current-${r.reportId}`}
                                                        onClick={() => openReport({ reportId: superseded.reportId, reportNo: superseded.reportNo, version: superseded.version })}
                                                        className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[10px] font-semibold text-amber-700 hover:underline cursor-pointer"
                                                    >
                                                        <ArrowUpRight className="w-3 h-3" />
                                                        {t('labreports.superseded_by', 'replaced by')} {superseded.reportNo}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={6} className="p-6 text-center text-gray-500">
                                            {t('labreports.no_reports', 'No released reports in this range. Widen the dates, or look on the worklist — a report that has not been released yet is still work in progress.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards below lg — the row carries seven columns and a tablet
                        at the collection counter is a real place this is read. */}
                    <div className="block lg:hidden space-y-4">
                        {reports.length > 0 ? reports.map((r) => {
                            const superseded = r.supersededBy || null;
                            return (
                                <div
                                    key={r.reportId}
                                    data-testid={`report-card-${r.reportId}`}
                                    className={`p-4 rounded-xl shadow-md ${superseded ? 'bg-amber-50/60 border border-amber-200' : 'bg-gray-50'}`}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => openReport(r)}
                                                className="font-mono font-semibold text-sm text-[#2D8A8A] hover:underline cursor-pointer"
                                            >
                                                {r.reportNo}
                                            </button>
                                            <div className="text-[11px] text-slate-400">{stampOf(r.releasedAt)}</div>
                                            <div className="text-xs text-gray-700 mt-1">{r.patientName || t('common.patient_label', 'Patient')}</div>
                                        </div>
                                        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold capitalize ${STATUS_STYLES[r.status] || STATUS_STYLES.draft}`}>
                                            {r.status}{Number(r.version) > 1 ? ` v${r.version}` : ''}
                                        </span>
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-2">
                                        {t('labreports.signed_by', 'Signed by')}: {r.signedByName || '-'}
                                        {r.signedByRegNo ? ` · ${t('labreports.reg_no', 'Reg.')} ${r.signedByRegNo}` : ''}
                                    </div>
                                    {superseded && (
                                        <button
                                            type="button"
                                            onClick={() => openReport({ reportId: superseded.reportId, reportNo: superseded.reportNo, version: superseded.version })}
                                            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:underline cursor-pointer"
                                        >
                                            <AlertTriangle className="w-3 h-3" />
                                            {t('labreports.superseded_card', 'Corrected — the current version is')} {superseded.reportNo}
                                        </button>
                                    )}
                                    <div className="flex gap-2 mt-3">
                                        <button onClick={() => openReport(r)} className="flex-1 py-2 rounded-xl bg-white border border-gray-200 text-xs text-[#2D8A8A] font-semibold cursor-pointer">
                                            {t('labreports.open_short', 'Open')}
                                        </button>
                                        <button onClick={() => handleViewPdf(r)} disabled={pdfBusyId === r.reportId} className="flex-1 py-2 rounded-xl bg-white border border-gray-200 text-xs text-[#D7A05C] font-semibold cursor-pointer">
                                            {t('labreports.view_pdf', 'View PDF')}
                                        </button>
                                        {canResend && (
                                            <button
                                                onClick={() => setResendTarget(r)}
                                                disabled={!!superseded}
                                                className={`flex-1 py-2 rounded-xl bg-white border border-gray-200 text-xs font-semibold ${superseded ? 'text-gray-400 cursor-not-allowed' : 'text-[#2D8A8A] cursor-pointer'}`}
                                            >
                                                {t('labreports.resend_short', 'Resend')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {t('labreports.no_reports', 'No released reports in this range. Widen the dates, or look on the worklist — a report that has not been released yet is still work in progress.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!listLoading && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            <ReportDrawer
                payload={drawer}
                loading={drawerLoading}
                onClose={closeDrawer}
                onViewPdf={handleViewPdf}
                onResend={canResend ? setResendTarget : null}
                onAmend={canAmend ? setAmendTarget : null}
                onOpenReport={openReport}
                pdfBusy={pdfBusyId === drawer?.report?.reportId}
                t={t}
            />

            <ResendDialog
                report={resendTarget}
                onClose={() => setResendTarget(null)}
                onSuperseded={(current) => { setResendTarget(null); openReport(current); }}
                t={t}
            />

            <LabSummaryModal
                reportId={summaryReportId}
                open={Boolean(summaryReportId)}
                onClose={() => setSummaryReportId(null)}
            />

            <AmendDialog
                report={amendTarget}
                canEnterResults={can('lab.result.enter')}
                onClose={() => setAmendTarget(null)}
                onGoToResults={(orderId) => {
                    setAmendTarget(null);
                    navigate(resultsRouteFor(orderId));
                }}
                t={t}
            />
        </div>
    );
}

/* ==================================================================== *
 * The report itself — the values, as they were signed.
 * ==================================================================== */

// The shell exists separately from the body so AnimatePresence has a mounted
// parent to animate the exit from. Returning null from one component that owns
// both would unmount the whole subtree the moment the report clears, and the
// drawer would vanish instead of sliding away.
function ReportDrawer({ payload, ...rest }) {
    return (
        <AnimatePresence>
            {payload && <ReportDrawerBody payload={payload} {...rest} />}
        </AnimatePresence>
    );
}

function ReportDrawerBody({ payload, loading, onClose, onViewPdf, onResend, onAmend, onOpenReport, pdfBusy, t }) {
    const report = payload.report || {};
    const results = payload.results || [];
    const specimen = payload.specimen || null;
    const patient = payload.patient || null;
    const superseded = payload.supersededBy || report.supersededBy || null;
    // Keyed on `payload` rather than on the two arrays inside it: `payload.results
    // || []` is a fresh array on every render when the detail call has not landed,
    // which would re-group on every keystroke elsewhere on the page.
    const blocks = useMemo(() => groupResults(payload.results || [], payload.sections || []), [payload]);
    const age = patient ? ageLabel(patient.ageDays) : null;

    return (
        <motion.div
            className="fixed inset-0 z-[9998] flex justify-end bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="report-drawer"
        >
            <motion.aside
                className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', duration: 0.25 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-lg font-bold text-gray-800 font-mono">{report.reportNo || '…'}</h2>
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold capitalize ${STATUS_STYLES[report.status] || STATUS_STYLES.draft}`}>
                                {report.status}
                            </span>
                            {Number(report.version) > 1 && (
                                <span className="inline-flex px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-[11px] font-semibold">
                                    {t('labreports.version', 'version')} {report.version}
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                            {stampOf(report.releasedAt)}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 cursor-pointer" aria-label={t('labreports.close', 'Close')}>
                        <X className="w-4 h-4 text-gray-500" />
                    </button>
                </div>

                {/* THE BANNER THAT MATTERS. Somebody reading this drawer may be
                    about to act on a number the laboratory has withdrawn. */}
                {superseded && (
                    <div data-testid="drawer-superseded-banner" className="mx-5 mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="text-[11px] leading-relaxed text-amber-800">
                                <p className="font-semibold">{t('labreports.superseded_title', 'This version has been corrected.')}</p>
                                <p>
                                    {t('labreports.superseded_body', 'It is kept because it was issued and read, but the laboratory now stands behind')}{' '}
                                    <button
                                        type="button"
                                        onClick={() => onOpenReport(superseded)}
                                        className="font-mono font-bold underline cursor-pointer"
                                    >
                                        {superseded.reportNo}
                                    </button>
                                    {superseded.version ? ` (${t('labreports.version', 'version')} ${superseded.version})` : ''}.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ------------------------------------- who and which tube ---- */}
                <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <Field
                        label={t('labreports.patient', 'Patient')}
                        value={[patient?.name || report.patientName || t('common.patient_label', 'Patient'), hospitalPatientCode(patient) || hospitalPatientCode(report)].filter(Boolean).join(' · ')}
                        Icon={User}
                    />
                    <Field
                        label={t('labreports.age_sex', 'Age / sex at collection')}
                        value={[age, patient?.gender].filter(Boolean).join(' · ')}
                    />
                    <Field
                        label={t('labreports.accession', 'Accession')}
                        value={specimen?.accessionNo}
                        mono
                    />
                    <Field
                        label={t('labreports.specimen', 'Specimen')}
                        value={specimen?.sampleType}
                    />
                    <Field label={t('labreports.collected_at', 'Collected')} value={stampOf(specimen?.collectedAt)} />
                    <Field label={t('labreports.received_at', 'Received')} value={stampOf(specimen?.receivedAt)} />
                    <Field
                        label={t('labreports.referred_by', 'Referred by')}
                        value={payload.referringDoctorName}
                        hint={payload.referringDoctorRegNo}
                        Icon={Stethoscope}
                    />
                    <Field label={t('labreports.released_at', 'Released')} value={stampOf(report.releasedAt)} />
                </div>

                {/* ------------------------------------------------ values ---- */}
                <div className="px-5 pb-4">
                    {loading && !results.length ? (
                        <div className="flex items-center gap-2 text-sm text-[#4ABDBD] py-6">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('labreports.loading_results', 'Loading the values…')}
                        </div>
                    ) : blocks.length === 0 ? (
                        <div className="py-6 text-center text-sm text-gray-500">
                            {t('labreports.no_results', 'No values are published on this report.')}
                        </div>
                    ) : blocks.map((block) => (
                        <div key={block.key} className="mb-5" data-testid={`report-section-${block.key}`}>
                            {block.name && (
                                <h3 className="text-[11px] font-bold uppercase tracking-wider text-[#2D8A8A] border-b border-[#4ABDBD]/30 pb-1 mb-2">
                                    {block.name}
                                </h3>
                            )}
                            {block.panels.map((panel) => (
                                <div key={panel.key} className="mb-3">
                                    {panel.name && (
                                        <div className="text-[11px] font-semibold text-gray-600 mb-1">{panel.name}</div>
                                    )}
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                                                    <th className="text-left font-semibold py-1">{t('labreports.test', 'Test')}</th>
                                                    <th className="text-right font-semibold py-1">{t('labreports.result', 'Result')}</th>
                                                    <th className="text-left font-semibold py-1 pl-3">{t('labreports.unit', 'Unit')}</th>
                                                    <th className="text-left font-semibold py-1">{t('labreports.reference', 'Reference')}</th>
                                                    <th className="text-left font-semibold py-1">{t('labreports.flag', 'Flag')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {panel.rows.map((row) => (
                                                    <tr key={row.resultId} data-testid={`result-row-${row.resultId}`} className="border-t border-gray-100">
                                                        <td className="py-1.5 pr-2 text-gray-700">
                                                            {row.testName || '—'}
                                                            {row.method && <div className="text-[10px] text-slate-400">{row.method}</div>}
                                                            {/* The disclosure a referral requires: the
                                                                patient is entitled to know which
                                                                laboratory actually measured this. */}
                                                            {row.isOutsourced && (
                                                                <div className="text-[10px] text-indigo-600">
                                                                    {t('labreports.outsourced', 'performed at')} {row.partnerName || t('labreports.partner_lab', 'a referral laboratory')}
                                                                </div>
                                                            )}
                                                            {row.amendedFromResultId && (
                                                                <div className="text-[10px] text-purple-600">
                                                                    {t('labreports.amended_value', 'amended')}{row.amendReason ? ` — ${row.amendReason}` : ''}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className={`py-1.5 text-right font-mono ${row.flag === 'critical_low' || row.flag === 'critical_high' ? 'text-red-700 font-bold' : 'text-gray-800 font-semibold'}`}>
                                                            {row.resultValue ?? '-'}
                                                        </td>
                                                        <td className="py-1.5 pl-3 text-slate-500">{row.unit || ''}</td>
                                                        <td className="py-1.5 text-slate-500 font-mono">{referenceText(row)}</td>
                                                        <td className="py-1.5">
                                                            {row.flag ? (
                                                                <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] ${FLAG_STYLES[row.flag] || FLAG_STYLES.normal}`}>
                                                                    {FLAG_LABELS[row.flag] || row.flag}
                                                                </span>
                                                            ) : (
                                                                // Blank, not "normal". No reference band
                                                                // matched this patient, so nothing was
                                                                // judged — saying "normal" would be an
                                                                // opinion the laboratory never formed.
                                                                <span className="text-[10px] text-slate-300">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}

                    {report.interpretation && (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 mb-4">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                                {t('labreports.interpretation', 'Interpretation')}
                            </div>
                            <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{report.interpretation}</p>
                        </div>
                    )}

                    {/* THE SIGNATURE. A pathology report is only a report because
                        somebody registered put their name and number on it. */}
                    <div className="rounded-xl border border-[#4ABDBD]/40 bg-[#4ABDBD]/5 p-3 mb-4" data-testid="report-signature">
                        <div className="flex items-start gap-2">
                            <ShieldCheck className="w-4 h-4 text-[#2D8A8A] flex-shrink-0 mt-0.5" />
                            <div className="text-[11px] leading-relaxed text-[#2D8A8A]">
                                <div className="font-semibold">{report.signedByName || t('labreports.unsigned', 'Not signed')}</div>
                                <div>
                                    {report.signedByRegNo
                                        ? `${t('labreports.reg_no', 'Reg.')} ${report.signedByRegNo}`
                                        : t('labreports.no_reg', 'no registration recorded')}
                                    {report.signedByType ? ` · ${report.signedByType}` : ''}
                                </div>
                                <div className="text-slate-500">
                                    {/* `releasedBy` is the releaser's user id — not drawn (no system
                                        id on screen); a name is shown when the server sends one. */}
                                    {report.releasedByName
                                        ? `${t('labreports.released_by', 'Released by')} ${report.releasedByName} · `
                                        : `${t('labreports.released_at', 'Released')} `}
                                    {stampOf(report.releasedAt)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 mb-2">
                        <button
                            data-testid="drawer-view-pdf"
                            onClick={() => onViewPdf(report)}
                            disabled={pdfBusy}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#D7A05C] text-[#b8863f] font-semibold text-sm hover:bg-[#D7A05C]/10 ${pdfBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                            {t('labreports.view_pdf', 'View PDF')}
                        </button>
                        {onAmend && (
                            <button
                                data-testid="drawer-amend"
                                onClick={() => onAmend(report)}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-purple-300 text-purple-700 font-semibold text-sm hover:bg-purple-50 cursor-pointer"
                            >
                                <FilePlus2 className="w-4 h-4" />
                                {t('labreports.amend_short', 'Amend')}
                            </button>
                        )}
                        {onResend && (
                            <button
                                data-testid="drawer-resend"
                                onClick={() => onResend(report)}
                                disabled={!!superseded}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] shadow-lg hover:shadow-xl ${superseded ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                <Send className="w-4 h-4" />
                                {t('labreports.resend', 'Send this report again')}
                            </button>
                        )}
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                        {t('labreports.footnote', 'Sending a copy re-delivers this report and keeps the number it already has. Correcting a value issues a new version instead — this one stays readable, because somebody has already acted on it.')}
                    </p>
                </div>
            </motion.aside>
        </motion.div>
    );
}

const Field = ({ label, value, hint, mono, Icon }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
            {Icon && <Icon className="w-3 h-3" />}
            {label}
        </div>
        <div className={`text-gray-700 font-semibold ${mono ? 'font-mono' : ''}`}>{value || '-'}</div>
        {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
    </div>
);

/* ==================================================================== *
 * Resend — the one action on this page that leaves the building.
 * ==================================================================== */

function ResendDialog({ report, onClose, onSuperseded, t }) {
    const dispatch = useDispatch();
    const [channel, setChannel] = useState('email');
    const [recipient, setRecipient] = useState('');
    const [sending, setSending] = useState(false);
    const [needsRecipient, setNeedsRecipient] = useState(false);

    useEffect(() => {
        if (report) {
            setChannel('email');
            setRecipient('');
            setNeedsRecipient(false);
        }
    }, [report]);

    if (!report) return null;

    const submit = async () => {
        setSending(true);
        try {
            const payload = await dispatch(resendReport({
                reportId: report.reportId,
                channel,
                recipient: recipient.trim(),
            })).unwrap();
            const data = payload?.data || {};
            // The confirmation names the report number back on purpose: the
            // person asked to send the report again, and the useful reassurance
            // is that it is the SAME report. Values are appended rather than
            // interpolated into the translation default, because a translated
            // string would drop what a default carries.
            const sentTo = [data.reportNo || report.reportNo, data.recipient].filter(Boolean).join(' → ');
            toast.success(`${t('labreports.resend_queued', 'A copy has been queued for delivery')}: ${sentTo}`);
            if (data.pdfAttached === false) {
                // The message still goes, but without the report in it. Worth
                // its own line: the patient will ask where the attachment is.
                toast.info(t('labreports.resend_nopdf', 'The PDF could not be attached to this copy'));
            }
            onClose();
        } catch (error) {
            // `unwrap()` throws the slice's rejection payload, which carries the
            // backend's `code` at the top level and its structured `data`
            // underneath. Reading them is the entire reason this dialog goes
            // through the slice: both branches below are refusals with a next
            // action attached, and a bare message string leaves the user to work
            // out which one they got.
            const code = error?.code;
            if (code === 'NO_RECIPIENT') {
                // Not a failure to explain away — it is a field this dialog now
                // has to ask for, so keep it open and point at the input.
                setNeedsRecipient(true);
            }
            // The version was corrected between the list loading and this click.
            // Refusing is right; leaving the user to find the current document
            // themselves is not, so the dialog hands them straight to it.
            const current = error?.data?.supersededBy;
            if (code === 'REPORT_SUPERSEDED' && current && onSuperseded) {
                toast.warn(errorText(error, t('labreports.resend_superseded_toast', 'This version has been corrected — opening the current one')));
                // `finally` still runs on the way out, so the sending flag is
                // cleared without saying so twice.
                onSuperseded(current);
                return;
            }
            toast.warn(errorText(error, t('labreports.resend_error', 'Failed to send the report')));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="report-resend-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5"
            >
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{t('labreports.resend_title', 'Send this report again')}</h2>
                        <p className="text-[11px] text-slate-400">{t('labreports.resend_sub', 'A copy goes out on the channel you pick.')}</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 cursor-pointer" aria-label={t('labreports.close', 'Close')}>
                        <X className="w-4 h-4 text-gray-500" />
                    </button>
                </div>

                {/* Said in the user's language, because somebody who does not know
                    it will keep asking for "a fresh report" until the screen
                    explains why it is the same one — and because two documents
                    in the world for one set of measurements is exactly what a
                    clinician cannot resolve at the bedside. */}
                <div className="rounded-xl border border-[#4ABDBD]/40 bg-[#4ABDBD]/5 p-3 mb-4" data-testid="resend-number-notice">
                    <div className="flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 text-[#2D8A8A] flex-shrink-0 mt-0.5" />
                        <p className="text-[11px] leading-relaxed text-[#2D8A8A]">
                            {t('labreports.resend_notice_1', 'The report keeps its number —')}{' '}
                            <span className="font-mono font-bold">{report.reportNo}</span>.{' '}
                            {t('labreports.resend_notice_2', 'This re-delivers the document; it does not re-issue it and it does not re-sign it. A corrected value is published as a new version instead.')}
                        </p>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{t('labreports.channel', 'Send by')}</label>
                    <div className="flex gap-2">
                        {CHANNELS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                data-testid={`report-resend-channel-${c}`}
                                onClick={() => setChannel(c)}
                                className={`flex-1 py-2 rounded-xl text-xs font-semibold capitalize border transition-all cursor-pointer ${channel === c
                                    ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white border-transparent shadow'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-[#4ABDBD]'}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {channel === 'email'
                            ? t('labreports.recipient_email', 'Email address')
                            : t('labreports.recipient_phone', 'Phone number')}
                        <span className="font-normal text-slate-400"> — {t('labreports.optional', 'optional')}</span>
                    </label>
                    <input
                        data-testid="report-resend-recipient"
                        type={channel === 'email' ? 'email' : 'tel'}
                        value={recipient}
                        onChange={(e) => { setRecipient(e.target.value); setNeedsRecipient(false); }}
                        placeholder={t('labreports.recipient_placeholder', 'Leave blank to use what is on file')}
                        className={`w-full p-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${needsRecipient ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    {needsRecipient && (
                        <p className="text-[11px] text-red-600 mt-1">
                            {t('labreports.no_recipient', 'Nothing is on file for this patient — type where the copy should go.')}
                        </p>
                    )}
                    {/* Typing an address here hands clinical data to whoever owns
                        it. The outbox row records exactly where it went, which is
                        why the action is permission-gated on the server. */}
                    <p className="text-[10px] text-slate-400 mt-1">
                        {t('labreports.recipient_note', 'Whatever you type is recorded against this report as the address the copy was sent to.')}
                    </p>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer"
                    >
                        {t('labreports.cancel', 'Cancel')}
                    </button>
                    <button
                        data-testid="report-resend-submit"
                        onClick={submit}
                        disabled={sending}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] shadow-lg hover:shadow-xl ${sending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {t('labreports.send_copy', 'Send a copy')}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

/* ==================================================================== *
 * Amend — the dialog whose entire job is to explain what amending IS.
 * ==================================================================== */

/**
 * There is no "edit" behind this button, and the dialog says so before it does
 * anything else.
 *
 * The temptation it exists to defuse is obvious: a wrong potassium is on a
 * released report, and the fastest thing anybody can imagine is opening it and
 * typing the right number. That is precisely what must not happen. The wrong
 * value was read, telephoned and acted on; a lab that overwrites it leaves the
 * clinician who acted on it looking at a document that no longer contains what
 * they saw, and the medical record then disagrees with the treatment given.
 *
 * So the correction runs the same road as the original: a NEW result row that
 * names the one it replaces, verified by somebody registered, released as a new
 * version with its own number. Three steps, in three different states, which is
 * why this dialog lists them rather than showing a form — the work belongs on
 * the results screen, and pretending otherwise here would be a second place
 * where the two-person rule could be got around.
 */
function AmendDialog({ report, canEnterResults, onClose, onGoToResults, t }) {
    if (!report) return null;

    const steps = [
        {
            n: 1,
            title: t('labreports.amend_step1_title', 'Record the corrected value'),
            body: t('labreports.amend_step1_body', 'On the results screen, amend the test. That writes a NEW result row pointing at the one it replaces, with your reason on it — the released value is never overwritten.'),
        },
        {
            n: 2,
            title: t('labreports.amend_step2_title', 'It is verified again'),
            body: t('labreports.amend_step2_body', 'A corrected value is unverified like any other. Somebody carrying a registration number has to verify it, and it may not be released before they do.'),
        },
        {
            n: 3,
            // The next version number is appended rather than interpolated into
            // the translation default: a translated string would drop the value
            // a default carries, and "issues version" with no number is worse
            // than the English sentence it replaced.
            title: `${t('labreports.amend_step3_title', 'Release issues version')} ${Number(report.version || 1) + 1}`,
            body: t('labreports.amend_step3_body', 'Releasing the order again publishes a new report with its own number and signature. This copy stays readable and is marked as replaced, so anybody holding it can see what superseded it.'),
        },
    ];

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="report-amend-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-5"
            >
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{t('labreports.amend_title', 'Amend this report')}</h2>
                        <p className="text-[11px] text-slate-400">
                            <span className="font-mono">{report.reportNo}</span>
                            {Number(report.version) > 1 ? ` · ${t('labreports.version', 'version')} ${report.version}` : ''}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 cursor-pointer" aria-label={t('labreports.close', 'Close')}>
                        <X className="w-4 h-4 text-gray-500" />
                    </button>
                </div>

                <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 mb-4" data-testid="amend-notice">
                    <div className="flex items-start gap-2">
                        <FilePlus2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                        <p className="text-[11px] leading-relaxed text-purple-800">
                            {t('labreports.amend_notice', 'Amending does not change this report. It issues a NEW version alongside it — because this one was signed, sent and very likely acted on, and a document somebody treated a patient from has to stay exactly as they read it.')}
                        </p>
                    </div>
                </div>

                <ol className="space-y-3 mb-4">
                    {steps.map((s) => (
                        <li key={s.n} className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white text-[11px] font-bold flex items-center justify-center">
                                {s.n}
                            </span>
                            <div>
                                <div className="text-xs font-semibold text-gray-700">{s.title}</div>
                                <p className="text-[11px] text-slate-500 leading-relaxed">{s.body}</p>
                            </div>
                        </li>
                    ))}
                </ol>

                {!canEnterResults && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4 text-[11px] text-amber-800">
                        {t('labreports.amend_no_permission', 'You may not record results yourself. Ask the bench that ran this test to raise the correction — this screen cannot make one on their behalf.')}
                    </div>
                )}

                <div className="flex gap-2">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer"
                    >
                        {t('labreports.amend_close', 'Leave it as it is')}
                    </button>
                    <button
                        data-testid="amend-go-to-results"
                        onClick={() => onGoToResults(report.orderId)}
                        disabled={!canEnterResults || !report.orderId}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-purple-500 to-fuchsia-500 shadow-lg hover:shadow-xl ${!canEnterResults || !report.orderId ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        <ArrowUpRight className="w-4 h-4" />
                        {t('labreports.amend_go', 'Open the results for this order')}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
